/**
 * The agent-facing tool for a turn that stops to ask a human.
 *
 * Lives in mod-core beside the other project tools because every host that
 * serves inference shares it: the daemon driving Claude Code, cli-next, a
 * hosted worker later. One `AskUserQuestion`, not a copy per surface.
 *
 * The mechanics are two log appends and a wait in between. `execute` appends
 * `mod.interaction_requested` and then polls the session document until an
 * answer with the same correlation id appears — written, in general, by a
 * DIFFERENT peer. Polling rather than subscribing is deliberate: Automerge
 * change events have proven unreliable across this codebase's surfaces, and a
 * missed event here would hang a turn forever. A poll cannot miss an edit that
 * is already in the document.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../log/branch-log'
import type { Interaction } from '../log/projection-interactions'
import type { SessionTaskLog } from '../model/tasks/mutations'
import {
  ASK_USER_QUESTION_DESCRIPTION,
  ASK_USER_QUESTION_INPUT_SCHEMA,
  buildInteractionTitle,
  buildQuestionBody,
  buildTimeoutMessage,
  buildToolResult,
  parseQuestionAnswer,
  parseToolInput,
  type QuestionBody,
  type ToolResult,
  validateToolInput,
} from './ask-user-question'
import type { ToolDef } from './project-tools'

/** How often the waiter re-reads the session document. */
const POLL_INTERVAL_MS = 400

/**
 * How long a question waits before giving up.
 *
 * Generous on purpose. The answering human may be on another device entirely —
 * that is the point of putting the question on the log — so the timeout is
 * "they are not coming back", not "they are slow". On expiry the model gets
 * Claude's own away-from-keyboard text, which tells it to proceed on its best
 * judgment rather than to ask again immediately.
 */
const DEFAULT_TIMEOUT_MS = 20 * 60 * 1000

export interface InteractionToolOptions {
  /** Override the wait before a question is abandoned. */
  timeoutMs?: number
  /** Cancel the wait when the turn is stopped from the composer. */
  signal?: AbortSignal
  /** Injected for tests; defaults to a real timer. */
  now?: () => number
  sleep?: (ms: number) => Promise<void>
  /**
   * The project's tasks, so a question asked with a `taskId` is written on the
   * durable work record (RFC 0304 3b). A task question is rejected without
   * this store because its answer could not be written back to the task.
   */
  tasks?: SessionTaskLog
  /**
   * The task log for the project that lists a task (RFC 0336 S1), when the
   * task may belong to another project than `tasks`. Falls back to `tasks`.
   */
  tasksFor?: (taskId: string) => Promise<SessionTaskLog>
}

/**
 * Prepare an open task for a question. Most questions leave the task active so
 * independent work can continue. The exceptional blocking form remembers the
 * exact status it owns, so an answer can restore that state and no other.
 */
async function prepareTaskQuestion(
  tasks: SessionTaskLog,
  taskId: string,
  sessionId: string,
  blocking: boolean,
): Promise<'active' | 'pending' | null> {
  const task = await tasks.get(taskId)
  if (!task) return null
  if (['complete', 'cancelled', 'blocked'].includes(task.status)) return null
  await tasks.update(taskId, {
    ...(blocking ? { status: 'blocked' as const } : {}),
    addRefs: [{ type: 'session', target: sessionId, role: 'worked-by' }],
  })
  return task.status as 'active' | 'pending'
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** A random correlation id. Not security-sensitive; it only pairs two entries. */
function newInteractionId(): string {
  return `int-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`
}

/**
 * Ask, then wait for the log to carry an answer.
 *
 * Returns the resolved interaction, or null when the wait expired. The request
 * is left on the log either way: an unanswered question is part of the record,
 * and a surface that opens later can still see what was asked.
 */
async function awaitAnswer(
  log: BranchLog,
  interactionId: string,
  opts: Required<Pick<InteractionToolOptions, 'now' | 'sleep'>> & {
    timeoutMs: number
    signal?: AbortSignal
  },
): Promise<Interaction | null> {
  const startedAt = opts.now()
  for (;;) {
    // Each pass reads the document afresh — `BranchLog.doc()` decodes from the
    // handle every call rather than caching — so an answer synced in from
    // another peer is visible on the very next poll.
    const current = log.interactionById(interactionId)
    if (current !== null && current.status !== 'pending') return current
    if (opts.signal?.aborted) return null
    if (opts.now() - startedAt >= opts.timeoutMs) return null
    await opts.sleep(POLL_INTERVAL_MS)
  }
}

/**
 * The outcome of putting a question to a human.
 *
 * Either they answered — and `result` is Claude's own result shape — or they
 * did not, and `message` is the text telling the model what to do about it
 * (Claude's away-from-keyboard line, a dismissal, or a steering message for
 * malformed input). Exactly one is present.
 */
export type AskOutcome =
  | { answered: true; result: ToolResult }
  | { answered: false; message: string; posted?: boolean }

type QuestionInput = ReturnType<typeof parseToolInput>

/**
 * What an `AskUserQuestion` call asks for, decided from its arguments alone.
 *
 * `wait`: the turn stops until the person answers. `post`: the question goes on
 * the log and the task's record, and the turn carries on (RFC 0304 3b); a task
 * is required because the answer needs a record to land on and a goal to
 * continue.
 */
export type AskMode =
  | { kind: 'wait'; input: QuestionInput }
  | {
      kind: 'post'
      input: QuestionInput
      taskId: string
      recommended: string
      blocking: boolean
    }
  | { kind: 'invalid'; message: string }

export function askMode(
  args: Record<string, unknown>,
  hasTaskStore: boolean,
): AskMode {
  const input = parseToolInput(args)
  const invalid = validateToolInput(input)
  if (invalid !== null) return { kind: 'invalid', message: invalid }

  const taskId = typeof args.taskId === 'string' ? args.taskId.trim() : ''
  if (!taskId) return { kind: 'wait', input }
  if (!hasTaskStore) {
    return { kind: 'invalid', message: 'taskId requires a task store.' }
  }
  const recommended =
    typeof args.recommended === 'string' ? args.recommended.trim() : ''
  if (!recommended) {
    return {
      kind: 'invalid',
      message: 'A task-linked question requires a recommended option.',
    }
  }
  const matches = input.questions.some((question) =>
    question.options.some((option) => option.label === recommended),
  )
  if (!matches) {
    return {
      kind: 'invalid',
      message: 'The recommended option must match one of the option labels.',
    }
  }
  return {
    kind: 'post',
    input,
    taskId,
    recommended,
    blocking: args.blocking === true,
  }
}

/** Append the question to the session's log; returns the log and its id. */
async function appendQuestion(
  repo: Repo,
  sessionId: string,
  body: QuestionBody,
): Promise<{ log: BranchLog; interactionId: string }> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const log = new BranchLog(handle as never)
  const interactionId = newInteractionId()
  log.appendInteractionRequested({
    interactionId,
    sessionId,
    kind: 'question',
    title: buildInteractionTitle(body),
    bodyJson: JSON.stringify(body),
  })
  return { log, interactionId }
}

/**
 * Put a question to a human: decide how from its arguments, then either wait
 * for the answer or post it on the task and return.
 *
 * The shared core of BOTH ways a question can reach a person:
 *
 *  - `interactionTools`, the Mod-owned `AskUserQuestion` offered over MCP to
 *    providers that have no native equivalent (Codex).
 *  - the interception of Claude Code's OWN native `AskUserQuestion`, which the
 *    daemon routes here from its permission callback (see `@mod/harness`).
 *
 * Both must behave identically — same validation, same steering text, same
 * entry on the log — so a question looks the same to the person answering it
 * whichever provider asked. That is only true if there is one implementation,
 * which is this one.
 */
export async function askOnLog(
  repo: Repo,
  sessionId: string,
  args: Record<string, unknown>,
  options: InteractionToolOptions = {},
): Promise<AskOutcome> {
  const mode = askMode(args, options.tasks !== undefined)
  switch (mode.kind) {
    case 'invalid':
      return { answered: false, message: mode.message }
    case 'post':
      return postQuestion(
        repo,
        sessionId,
        mode,
        options.tasksFor
          ? await options.tasksFor(mode.taskId)
          : (options.tasks as SessionTaskLog),
        options.now ?? (() => Date.now()),
      )
    case 'wait':
      return askAndWait(repo, sessionId, mode.input, options)
  }
}

/**
 * Post a task question and return straight away. The question goes on the
 * session's log and the task's record; the answer arrives as a later turn.
 */
async function postQuestion(
  repo: Repo,
  sessionId: string,
  mode: Extract<AskMode, { kind: 'post' }>,
  tasks: SessionTaskLog,
  now: () => number,
): Promise<AskOutcome> {
  const { input, taskId, recommended, blocking } = mode
  const resumeStatus = await prepareTaskQuestion(
    tasks,
    taskId,
    sessionId,
    blocking,
  )
  if (!resumeStatus) {
    return { answered: false, message: `Task ${taskId} is not open.` }
  }
  const body = {
    ...buildQuestionBody(input),
    taskId,
    recommended,
    blocking,
    ...(blocking ? { resumeStatus } : {}),
  }
  const { interactionId } = await appendQuestion(repo, sessionId, body)
  await tasks.recordEvent(taskId, {
    type: 'asked',
    at: new Date(now()).toISOString(),
    by: sessionId,
    refs: [{ type: 'interaction', target: interactionId }],
    note: `${input.questions.map((question) => question.question).join(' / ')} Recommended: ${recommended}.`,
  })
  return {
    answered: false,
    posted: true,
    message:
      `Question ${interactionId} was posted${blocking ? ` and task ${taskId} is blocked` : `; task ${taskId} remains actionable`}. ` +
      'Continue any work that does not depend on the answer; the answer will queue a new turn.',
  }
}

/** Ask, and hold the turn until the person answers, dismisses, or time runs out. */
async function askAndWait(
  repo: Repo,
  sessionId: string,
  input: QuestionInput,
  options: InteractionToolOptions,
): Promise<AskOutcome> {
  const now = options.now ?? (() => Date.now())
  const sleep = options.sleep ?? defaultSleep
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const body = buildQuestionBody(input)
  const askedAt = now()
  const { log, interactionId } = await appendQuestion(repo, sessionId, body)

  const resolved = await awaitAnswer(log, interactionId, {
    now,
    sleep,
    timeoutMs,
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (resolved === null) {
    // Record the expiry so the question stops rendering as pending on every
    // surface syncing this session — otherwise a phone would show a live
    // prompt for a turn that has already moved on.
    log.appendInteractionAnswered({
      interactionId,
      sessionId,
      outcome: 'cancelled',
      reason: options.signal?.aborted ? 'dismissed' : 'timeout',
    })
    return {
      answered: false,
      message: options.signal?.aborted
        ? 'The turn was stopped before the question was answered.'
        : buildTimeoutMessage(now() - askedAt),
    }
  }

  if (resolved.status === 'cancelled') {
    return {
      answered: false,
      message:
        resolved.answer?.reason === 'timeout'
          ? buildTimeoutMessage(now() - askedAt)
          : 'The user dismissed the question without answering. Proceed with your best judgement, or ask again in your reply.',
    }
  }

  const answer = parseQuestionAnswer(resolved.answer?.answerJson ?? '')
  const result = buildToolResult(body, answer)
  if (Object.keys(result.answers).length === 0) {
    return {
      answered: false,
      message:
        'The user submitted no answers. Proceed with your best judgement, or ask again in your reply.',
    }
  }
  return { answered: true, result }
}

/**
 * `AskUserQuestion` as a Mod tool, scoped to one session's log.
 *
 * Session-scoped rather than project-scoped (unlike the document and app
 * tools) because a question belongs to the turn that asked it: it renders in
 * that session's thread and resolves that session's block.
 *
 * NOTE: for Claude Code this is the second-choice path. Claude Code ships its
 * own native `AskUserQuestion`, and a model that knows it will call THAT rather
 * than an MCP tool of the same name (observed: the native call returns
 * "nothing to click" in a non-interactive SDK session, and no question ever
 * reaches the log). The daemon therefore intercepts the native call and routes
 * it to `askOnLog` instead. This tool remains for providers with no native
 * equivalent.
 */
export function interactionTools(
  repo: Repo,
  sessionId: string,
  options: InteractionToolOptions = {},
): Record<string, ToolDef> {
  return {
    AskUserQuestion: {
      description: ASK_USER_QUESTION_DESCRIPTION,
      inputSchema: ASK_USER_QUESTION_INPUT_SCHEMA,
      execute: async (args) => {
        const outcome = await askOnLog(repo, sessionId, args, options)
        // Steering text is returned as a STRING, not thrown: Claude's messages
        // are instructions about what to do next, and a thrown error reads as a
        // malfunction rather than as guidance.
        return outcome.answered
          ? JSON.stringify(outcome.result)
          : outcome.message
      },
    },
  }
}
